import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { en } from './languages/en';
import { de } from './languages/de';

// Available languages
export const LANGUAGES = {
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇬🇧'
  },
  de: {
    code: 'de',
    name: 'German',
    nativeName: 'Deutsch',
    flag: '🇩🇪'
  }
  // Future languages can be added here:
  // fr: { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
};

// Language files
const languageFiles = {
  en,
  de
  // Future language files:
  // fr: () => import('./languages/fr').then(m => m.fr),
};

// Language Context
export const LanguageContext = createContext();

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Language Provider Component
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translations, setTranslations] = useState(en);
  const [isLoading, setIsLoading] = useState(false);

  // Load language function
  const loadLanguage = useCallback(async (languageCode) => {
    console.log('LanguageProvider: Loading language:', languageCode);
    if (!LANGUAGES[languageCode]) {
      console.warn(`Language ${languageCode} not supported`);
      return;
    }

    setIsLoading(true);
    try {
      const languageFile = languageFiles[languageCode];
      console.log('LanguageProvider: Language file:', languageFile);
      if (typeof languageFile === 'function') {
        // For dynamically imported languages
        const module = await languageFile();
        setTranslations(module);
        console.log('LanguageProvider: Loaded dynamic language:', languageCode);
      } else {
        // For statically imported languages
        setTranslations(languageFile);
        console.log('LanguageProvider: Loaded static language:', languageCode, languageFile);
      }
    } catch (error) {
      console.error(`Error loading language ${languageCode}:`, error);
      // Fallback to English
      setTranslations(en);
      setCurrentLanguage('en');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Function to get language from URL parameters
  const getLanguageFromURL = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const langFromURL = urlParams.get('lang');
    console.log('LanguageProvider: Language from URL:', langFromURL);
    return langFromURL && LANGUAGES[langFromURL] ? langFromURL : null;
  }, []);

  // Load language from URL or localStorage on mount
  useEffect(() => {
    // First priority: URL parameter
    const urlLanguage = getLanguageFromURL();
    if (urlLanguage) {
      console.log('LanguageProvider: Using language from URL:', urlLanguage);
      setCurrentLanguage(urlLanguage);
      localStorage.setItem('privacv-language', urlLanguage);
      loadLanguage(urlLanguage);
      return;
    }

    // Second priority: localStorage
    const savedLanguage = localStorage.getItem('privacv-language');
    console.log('LanguageProvider: Saved language from localStorage:', savedLanguage);
    if (savedLanguage && LANGUAGES[savedLanguage]) {
      setCurrentLanguage(savedLanguage);
      loadLanguage(savedLanguage);
    } else {
      console.log('LanguageProvider: No saved language, using default (en)');
    }
  }, [loadLanguage, getLanguageFromURL]);

  // Listen for URL changes (browser navigation)
  useEffect(() => {
    const handlePopState = () => {
      const urlLanguage = getLanguageFromURL();
      if (urlLanguage && urlLanguage !== currentLanguage) {
        console.log('LanguageProvider: URL changed, updating language to:', urlLanguage);
        setCurrentLanguage(urlLanguage);
        localStorage.setItem('privacv-language', urlLanguage);
        loadLanguage(urlLanguage);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentLanguage, getLanguageFromURL, loadLanguage]);

  // Change language function
  const changeLanguage = async (languageCode) => {
    console.log('LanguageProvider: Changing language from', currentLanguage, 'to', languageCode);
    if (languageCode === currentLanguage) return;
    
    setCurrentLanguage(languageCode);
    localStorage.setItem('privacv-language', languageCode);
    
    // Update URL parameter
    const url = new URL(window.location);
    url.searchParams.set('lang', languageCode);
    window.history.pushState({}, '', url.toString());
    console.log('LanguageProvider: Updated URL to include lang parameter:', languageCode);
    
    await loadLanguage(languageCode);
    console.log('LanguageProvider: Language changed successfully to', languageCode);
  };

  // Utility function to interpolate variables in strings
  const interpolate = (template, variables = {}) => {
    if (typeof template !== 'string') return template;
    
    return template.replace(/\{(\w+)\}/g, (match, key) => {
      return variables[key] !== undefined ? variables[key] : match;
    });
  };

  // Helper function to get legal common variables
  const getLegalVariables = () => {
    const common = translations?.legal?.common || {};
    return {
      companyName: common.companyName || 'Navid Sadat-Razavi',
      companyAddress: common.companyAddress || 'Kopischstr. 1',
      companyCity: common.companyCity || '10965 Berlin',
      companyCountry: common.companyCountry || (currentLanguage === 'de' ? 'Deutschland' : 'Germany'),
      contactEmail: common.contactEmail || 'info@privacv.com',
      pdfPrice: common.pdfPrice || '€4.99',
      currency: common.currency || '€',
      websiteUrl: common.websiteUrl || (currentLanguage === 'de' ? 'privacv.de' : 'privacv.com'),
      date: currentLanguage === 'de' ? 'Dezember 2024' : 'December 2024'
    };
  };

  // Translation function with nested key support and variable interpolation
  const t = (key, variables = {}, defaultValue = '') => {
    try {
      const keys = key.split('.');
      let value = translations;
      
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          console.warn(`Translation key not found: ${key}`);
          return defaultValue || key;
        }
      }
      
      const result = typeof value === 'string' ? value : defaultValue || key;
      
      // If variables are provided, interpolate them
      if (Object.keys(variables).length > 0) {
        return interpolate(result, variables);
      }
      
      return result;
    } catch (error) {
      console.warn(`Translation key error: ${key}`, error);
      return defaultValue || key;
    }
  };

  const value = {
    currentLanguage,
    translations,
    availableLanguages: LANGUAGES,
    changeLanguage,
    t,
    getLegalVariables,
    isLoading
  };

  console.log('LanguageProvider: Context value:', {
    currentLanguage,
    hasTranslations: !!translations,
    translationsKeys: translations ? Object.keys(translations).slice(0, 5) : [],
    isLoading
  });

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider; 